import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
} from '@angular/core';
import { BaseComponent } from '@core/base.component';
import { CapacityTypeLabels } from '@core/util/capacity';
import { ICON } from '@core/util/icon';
import {
	MatterERegTab,
	MatterFileType,
	MatterTitle,
	MatterTransferInstrument,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, select, dispatch } from '@ngxs/store';
import { NavigationService } from '@services/nav/nav.service';
import { OverwriteFields } from '@state/matter/matter.actions';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { DRAWER_DATA } from 'src/app/ui/drawer/drawer-data.token';

export interface ReviewTransferFieldsComponentData {
	matterId: string;
	transferObject: MatterTransferInstrument;
	interestEstate: string;
	matterType: MatterFileType;
	matterTitles: MatterTitle[];
	missingFields: {
		message: string;
		action: () => void;
	}[];
}

interface VM {
	interestEstate: string;
	transferDetails: MatterERegTab;
	transferObject: MatterTransferInstrument;
	defaultActingFor: string;
	defaultFeePayment: string;
	matterTitles?: MatterTitle[];
	pinsText?: string;
	missingFields: {
		message: string;
		action: () => void;
	}[];
}

const defaultVM: VM = {
	interestEstate: '',
	transferDetails: null,
	transferObject: null,
	defaultActingFor: 'T',
	defaultFeePayment: 'T',
	matterTitles: [],
	pinsText: '',
	missingFields: [],
};

@UntilDestroy()
@Component({
	selector: 'app-review-transfer-fields',
	templateUrl: './review-transfer-fields.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ReviewTransferFieldsComponent
	extends BaseComponent<VM>
	implements OnInit
{
	private overwriteFields = dispatch(OverwriteFields);

	public actingParties = [
		{ value: 'T', label: 'Parties to' },
		{ value: 'F', label: 'Parties from' },
	];
	public MatterFileType = MatterFileType;
	public CapacityTypeLabels = CapacityTypeLabels;
	public ICON = ICON;

	@Select(SelectedMatter.transferDetails)
	transferDetails$: Observable<MatterERegTab>;
	@Select(SelectedMatter.capacity) capacity$: Observable<string>;
	@Select(SelectedMatter.interestEstate) interestEstate$: Observable<string>;
	public matterFileType_ = select(SelectedMatter.matterFileType);
	public matterId_ = select(SelectedMatter.id);

	constructor(
		private navigationService: NavigationService,
		@Inject(DRAWER_DATA) private data: ReviewTransferFieldsComponentData,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.VM = {
			transferObject: this.data.transferObject,
			interestEstate: this.data.interestEstate,
			defaultActingFor:
				this.matterFileType_() === MatterFileType.SALE ? 'F' : 'T',
			defaultFeePayment: 'T',
			matterTitles: this.data.matterTitles,
			missingFields: this.data.missingFields,
			pinsText: this.data.matterTitles?.length > 1 ? 'PINs' : 'PIN',
		};

		this.transferDetails$
			.pipe(untilDestroyed(this))
			.subscribe((transferDetails) => {
				this.VM = {
					transferDetails,
				};
			});
	}

	goToMatterOverview = () => {
		this.navigationService.goToMatter_Overview(this.matterId_());
	};

	goToMatterPropertyDetails = () => {
		this.navigationService.goToMatter_Property(this.matterId_(), {});
	};

	goToClients = () => {
		this.navigationService.goToMatter_Parties(this.matterId_());
	};

	goToSellers = () => {
		if (this.matterFileType_() === MatterFileType.SALE) {
			this.navigationService.goToMatter_Parties(this.matterId_());
		} else {
			this.navigationService.goToMatter_Parties_OtherParties(this.matterId_());
		}
	};

	goToStatementOfAdjustment = () => {
		this.navigationService.goToMatter_Adjustments(this.matterId_());
	};

	public onChangeActingFor(actingFor): void {
		this.overwriteFields({
			field: 'ereg.transfer.actingFor',
			value: actingFor,
		});
	}

	public onChangeFeePayment(feePayment): void {
		this.overwriteFields({
			field: 'ereg.transfer.feePayment',
			value: feePayment,
		});
	}

	public updateDocumentName(event: string): void {
		this.overwriteFields({ field: 'ereg.transfer.documentName', value: event });
	}
}
